import { Check, ChevronDown, Loader2, Search } from 'lucide-react';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import type {
  BusinessRelationshipDetail,
  TradeSort,
} from '@/entities/business-relationship/api/businessRelationship.api';
import { useTrades } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import type { RelationshipGroup } from '@/entities/business-relationship/lib/statusView';
import {
  toTradeSections,
  type TradeRow,
} from '@/entities/business-relationship/lib/tradeRows';
import { AppOnlyButton } from '@/shared/components/app-guide/AppOnlyButton';
import { useDrugDrawer } from '@/shared/components/drug-drawer/useDrugDrawer';
import { EmptyState, ErrorState } from '@/shared/components/StateViews';
import { Badge } from '@/shared/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { SearchInput } from '@/shared/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
} from '@/shared/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { cn } from '@/shared/lib/utils';
import { formatWon } from '@/shared/utils/format';
import { useUIStore } from '@/stores/uiStore';

import { formatSpacedDate } from '../utils';
import { DrawerHeader } from './drawerParts';

const SECTION_ORDER: RelationshipGroup[] = ['ongoing', 'applying', 'closed'];
const SECTION_LABEL: Record<RelationshipGroup, string> = {
  ongoing: '거래중',
  applying: '필터링 신청',
  closed: '종료·거절',
};

const SORT_LABEL: Record<TradeSort, string> = {
  APPROVAL_DATE_DESC: '승인일 최신순',
  APPLICATION_DATE_DESC: '신청일 최신순',
  NAME_ASC: '이름순',
};
/** 정렬을 고르지 않으면 서버 기본값 — 의원급 승인일 · 병원급 신청일 최신순 */
const defaultSort = (isHospital: boolean): TradeSort =>
  isHospital ? 'APPLICATION_DATE_DESC' : 'APPROVAL_DATE_DESC';

const pad2 = (n: number) => String(n).padStart(2, '0');

const emptyMessage = (section: RelationshipGroup, isHospital: boolean) => {
  const subject = isHospital ? '의약품이' : '제약사가';
  switch (section) {
    case 'ongoing':
      return `거래중인 ${subject} 없어요.`;
    case 'applying':
      return `필터링 신청 중인 ${subject} 없어요.`;
    default:
      return `종료되거나 거절된 ${subject} 없어요.`;
  }
};

/** 행 한 줄 — 행 구조 · 간격 · 배지 색은 의원급 · 병원급이 같다 (M-20 정책) */
const TradeRowItem = ({
  row,
  onOpenDrug,
}: {
  row: TradeRow;
  onOpenDrug?: (drugCode: string) => void;
}) => {
  const date = formatSpacedDate(row.date);
  const meta = [
    row.pharmaCompanyName,
    date && `${date} ${row.dateSuffix}`,
    row.group !== 'closed' &&
      (row.transferPending
        ? '타 CSO 이관 확인 중'
        : row.minPrescriptionAmount !== null &&
          `최소 처방 금액 ${formatWon(row.minPrescriptionAmount)}`),
  ].filter(Boolean) as string[];

  const clickable = !!onOpenDrug && !!row.drugCode;

  const body = (
    <>
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <span className="min-w-0 text-[15px] leading-[22px] font-medium break-keep text-ink">
            {row.title}
          </span>
        </div>
        <Badge tone={row.badgeTone}>{row.badgeLabel}</Badge>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <p className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 text-xs leading-[18px] text-ink-6">
          {meta.map((m, i) => (
            <span
              key={m}
              className={cn(
                'flex items-center gap-2',
                i === 0 && row.pharmaCompanyName && 'font-medium text-ink-3',
              )}
            >
              {i > 0 && <span className="text-ink-faint">·</span>}
              {m}
            </span>
          ))}
        </p>
        {row.group === 'closed' && (
          <AppOnlyButton feature="filteringApply" className="w-[116px]">
            필터링 신청
          </AppOnlyButton>
        )}
      </div>
    </>
  );

  if (clickable) {
    return (
      <li className="border-b border-line-soft">
        <button
          type="button"
          onClick={() => onOpenDrug(row.drugCode!)}
          className="block w-full px-6 py-[18px] text-left outline-none hover:bg-subtle focus-visible:bg-subtle"
        >
          {body}
        </button>
      </li>
    );
  }
  return <li className="border-b border-line-soft px-6 py-[18px]">{body}</li>;
};

/**
 * M-19 · M-20 거래 정보 드로어 (640) — 조회 전용.
 * 의원급은 제약사, 병원급은 의약품 단위. GET /trades가 세 섹션을 한 번에 주므로 모두 그리고,
 * 탭은 그 섹션으로 스크롤만 한다. 검색 · 정렬은 서버에서 한다.
 * 검색 · 정렬은 닫았다 열어도 유지한다 (M-12에서 돌아올 때도 그대로).
 * 병원급 거래중 행을 누르면 M-12 드로어가 이 드로어 위에 24px 비켜 겹친다 (M-15 B 2단).
 */
export const TradeInfoDrawer = ({
  detail,
  isHospital,
  section,
  onClose,
}: {
  detail: BusinessRelationshipDetail;
  isHospital: boolean;
  /** 열 때 스크롤할 섹션. null이면 닫힘 */
  section: RelationshipGroup | null;
  onClose: () => void;
}) => {
  const open = section !== null;
  const unit = isHospital ? '의약품' : '제약사';
  const [keyword, setKeyword] = useState('');
  const [sort, setSort] = useState<TradeSort>();
  const [active, setActive] = useState<RelationshipGroup>('ongoing');
  const setStacked = useUIStore((s) => s.setDrugDrawerStacked);
  const { open: openDrug } = useDrugDrawer();
  const { drug } = useSearch({ strict: false }) as { drug?: string };
  /** 위에 겹친 M-12 드로어가 닫히면 포커스를 눌렀던 행으로 돌려준다 */
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Partial<Record<RelationshipGroup, HTMLElement>>>(
    {},
  );

  const query = useDebouncedValue(keyword.trim());
  const params = { keyword: query || undefined, sort };
  const trades = useTrades(detail.relationshipId, params, open);
  // 검색 · 정렬 전 첫 화면은 상세 응답의 거래 섹션으로 바로 그린다
  const untouched = !params.keyword && !params.sort;
  const source = trades.data ?? (untouched ? detail.trades : undefined);
  const sections = useMemo(
    () => (source ? toTradeSections(source, isHospital) : null),
    [source, isHospital],
  );
  const totalCounts = trades.data?.totalCounts ?? detail.tradeStatusCounts;
  const countOf = (s: RelationshipGroup) =>
    params.keyword && sections ? sections[s].length : totalCounts[s];

  const openStackedDrug = (code: string) => {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    void openDrug(code);
  };

  useEffect(() => {
    const target = returnFocusRef.current;
    if (drug || !open || !target) return;
    returnFocusRef.current = null;
    const id = window.setTimeout(() => target.focus(), 0);
    return () => window.clearTimeout(id);
  }, [drug, open]);

  // 이 드로어가 열려 있는 동안 M-12 드로어는 2단 스택으로 연다
  useEffect(() => {
    setStacked(open);
    return () => setStacked(false);
  }, [open, setStacked]);

  const scrollTo = (s: RelationshipGroup, behavior: ScrollBehavior) => {
    setActive(s);
    const container = scrollRef.current;
    const el = sectionRefs.current[s];
    if (!container || !el) return;
    // 스크롤 영역이 relative라 offsetTop은 영역 기준이다
    container.scrollTo({ top: el.offsetTop, behavior });
  };

  // 카드에서 열면 그 섹션으로 바로 내려간다 (Sheet가 내용을 그린 다음 프레임)
  useEffect(() => {
    if (!section) return;
    const id = window.requestAnimationFrame(() => scrollTo(section, 'auto'));
    return () => window.cancelAnimationFrame(id);
    // sections: 첫 응답이 와서 높이가 정해진 뒤에도 한 번 맞춘다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, !!sections]);

  // 스크롤 위치에 따라 탭 표시를 바꾼다 — 화면 위에 닿은 마지막 섹션
  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const top = container.scrollTop + 8;
    // 스크롤이 생길 만큼 길 때만 끝에 닿으면 마지막 섹션으로 본다
    const atBottom =
      container.scrollHeight > container.clientHeight &&
      container.scrollTop + container.clientHeight >=
        container.scrollHeight - 2;
    let current: RelationshipGroup = 'ongoing';
    SECTION_ORDER.forEach((s) => {
      const el = sectionRefs.current[s];
      if (el && el.offsetTop <= top) current = s;
    });
    setActive(atBottom ? 'closed' : current);
  };

  // 검색 · 정렬로 목록 길이가 바뀌면 탭 표시를 다시 맞춘다
  useEffect(() => {
    handleScroll();
  }, [sections]);

  const sortValue = sort ?? defaultSort(isHospital);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        overlay="drawer"
        // 위에 M-12 드로어가 겹치면 포커스가 그쪽으로 옮겨 간다. 그때 이 드로어가 닫히지 않게 한다
        onFocusOutside={(e) => e.preventDefault()}
      >
        <DrawerHeader title="거래 정보" onClose={onClose}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm leading-[21px] font-medium text-ink">
              {detail.clinicName}
            </span>
            <Badge tone={isHospital ? 'success' : 'primary'}>
              {isHospital ? '병원급' : '의원급'}
            </Badge>
            <span className="text-xs leading-[18px] text-ink-6">
              거래 단위 = {unit}
            </span>
          </div>
        </DrawerHeader>
        <SheetDescription className="sr-only">
          {detail.clinicName}의 거래 상태별 {unit} 목록
        </SheetDescription>

        <Tabs
          value={active}
          onValueChange={(v) => scrollTo(v as RelationshipGroup, 'smooth')}
          className="mt-3 shrink-0"
        >
          <TabsList className="grid grid-cols-3 gap-0 border-b-2">
            {SECTION_ORDER.map((s) => (
              <TabsTrigger
                key={s}
                value={s}
                className="group -mb-[2px] pt-1 pb-3 text-center"
              >
                {SECTION_LABEL[s]}{' '}
                <span className="font-normal text-ink-7 group-data-[state=active]:font-bold group-data-[state=active]:text-primary">
                  {pad2(countOf(s))}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex shrink-0 items-center gap-2.5 border-b border-line-soft px-6 py-4">
          <SearchInput
            className="flex-1"
            icon={
              trades.isFetching && !!params.keyword ? (
                <Loader2 className="size-3.5 shrink-0 animate-spin text-ink-6" />
              ) : (
                <Search className="size-3.5 shrink-0 text-ink-6" />
              )
            }
            placeholder={
              isHospital ? '의약품 · 제약사 명으로 검색' : '제약사 명으로 검색'
            }
            aria-label={
              isHospital ? '의약품 · 제약사 명으로 검색' : '제약사 명으로 검색'
            }
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <DropdownMenu>
            <DropdownMenuTrigger className="flex h-[42px] shrink-0 items-center gap-1 rounded-md border border-line-strong bg-surface px-3 text-xs leading-[18px] font-medium whitespace-nowrap text-ink-2 outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
              {SORT_LABEL[sortValue]}
              <ChevronDown className="size-3.5 text-ink-6" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(Object.keys(SORT_LABEL) as TradeSort[]).map((key) => (
                <DropdownMenuItem key={key} onSelect={() => setSort(key)}>
                  {SORT_LABEL[key]}
                  {sortValue === key && <Check className="ml-auto size-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="relative flex-1 overflow-y-auto"
        >
          {!sections ? (
            trades.isError ? (
              <ErrorState onRetry={() => void trades.refetch()} />
            ) : (
              <div className="flex justify-center py-16">
                <Loader2 className="size-5 animate-spin text-ink-7" />
              </div>
            )
          ) : params.keyword && trades.data?.matchedCount === 0 ? (
            <EmptyState className="py-16" message="검색 결과가 없어요." />
          ) : (
            SECTION_ORDER.map((s) => (
              <section
                key={s}
                ref={(el) => {
                  if (el) sectionRefs.current[s] = el;
                }}
                aria-label={SECTION_LABEL[s]}
              >
                <h3 className="sticky top-0 z-[1] flex items-center border-b border-line-soft bg-subtle px-6 py-2.5 text-xs leading-[18px] font-bold text-ink-4">
                  {SECTION_LABEL[s]}
                  <span className="ml-1.5 font-normal text-ink-6">
                    {sections[s].length}
                  </span>
                </h3>
                {sections[s].length === 0 ? (
                  <p className="border-b border-line-soft px-6 py-6 text-center text-xs leading-[18px] text-ink-6">
                    {params.keyword
                      ? '검색 결과가 없어요.'
                      : emptyMessage(s, isHospital)}
                  </p>
                ) : (
                  <ul>
                    {sections[s].map((row) => (
                      <TradeRowItem
                        key={row.key}
                        row={row}
                        onOpenDrug={
                          isHospital && s === 'ongoing'
                            ? openStackedDrug
                            : undefined
                        }
                      />
                    ))}
                  </ul>
                )}
              </section>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
